import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAttendanceReport,
  getPayrollListByStaffId,
  getSingleStaffAllAttendanceReport,
  getPayrollStaffList,
  getPayrollHistoryList,
  payStaffPayroll,
} from "@/api/new-api/staff-management/attendance-report/attendance";
import { getAllStaff } from "@/api/staff";

export const fetchAttendanceReport = createAsyncThunk(
  "attendancePayroll/fetchAttendanceReport",
  async ({ restaurant_id, date, page, limit }, { rejectWithValue }) => {
    const res = await getAttendanceReport(restaurant_id, date, page, limit);
    if (res.status === "success") return res.data || {};
    return rejectWithValue(res.message);
  }
);

export const fetchSingleStaffAllAttendanceReport = createAsyncThunk(
  "attendancePayroll/fetchSingleStaffAllAttendanceReport",
  async ({ staff_id, date, page = 1, limit = 10 }, { rejectWithValue }) => {
    const res = await getSingleStaffAllAttendanceReport(
      staff_id,
      date,
      page,
      limit
    );
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

export const fetchPayrollListByStaffId = createAsyncThunk(
  "attendancePayroll/fetchPayrollListByStaffId",
  async ({ staff_id, page = 1 }, { rejectWithValue }) => {
    const res = await getPayrollListByStaffId(staff_id, page);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

export const fetchPayrollStaffList = createAsyncThunk(
  "attendancePayroll/fetchPayrollStaffList",
  async (
    {
      restaurant_id,
      month,
      year,
      per_page = 10,
      sort_field = "id",
      sort_order = "desc",
      search_term = "",
      page = 1,
    },
    { rejectWithValue }
  ) => {
    const res = await getPayrollStaffList({
      restaurant_id,
      month,
      year,
      per_page,
      sort_field,
      sort_order,
      search_term,
      page,
    });
    if (res.status === "success") return res.data || {};
    return rejectWithValue(res.message);
  }
);

export const fetchPayrollHistoryList = createAsyncThunk(
  "attendancePayroll/fetchPayrollHistoryList",
  async (
    {
      restaurant_id,
      month,
      year,
      per_page = 10,
      sort_field = "id",
      sort_order = "desc",
      search_term = "",
      page = 1,
    },
    { rejectWithValue }
  ) => {
    const res = await getPayrollHistoryList({
      restaurant_id,
      month,
      year,
      per_page,
      sort_field,
      sort_order,
      search_term,
      page,
    });
    if (res.status === "success") return res.data || {};
    return rejectWithValue(res.message);
  }
);

// ✅ Get all staff
export const fetchAllStaff = createAsyncThunk(
  "staff/fetchAllStaff",
  async (_, { rejectWithValue }) => {
    const res = await getAllStaff();
    if (res.status === "success") return res.data.allStaff;
    return rejectWithValue(res.message);
  }
);

export const payStaffPayrollAction = createAsyncThunk(
  "attendancePayroll/payStaffPayroll",
  async ({ restaurant_id, item_id, payslip_id }, { rejectWithValue }) => {
    const res = await payStaffPayroll(restaurant_id, payslip_id || item_id);
    if (res.status === "success") {
      return { item_id, message: res.message || "Payment processed successfully." };
    }
    return rejectWithValue(res.message);
  }
);

const initialState = {
  attendanceReport: {},
  singleStaffAttendance: [],
  singleStaffattendanceStatistics: {},
  staffPayrollList: [],
  payrollPagination: null,
  singleStaffPagination: null,
  
  attendanceLoading: false,
  singleStaffLoading: false,
  payrollLoading: false,

  payrollStaffs: [],
  payrollStaffPagination: null,
  payrollStaffLoading: false,

  payrollHistoryStaffs: [],
  payrollHistoryPagination: null,
  payrollHistoryLoading: false,

  success: null,
  error: null,
  requestStatus: false,
  
  staffList: [],
  staffLoading: false,
  staffError: null
};

const attendancePayrollSlice = createSlice({
  name: "attendancePayroll",
  initialState,
  reducers: {
    clearAttendancePayrollMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllStaff.pending, (state) => {
        state.staffLoading = true;
      })
      .addCase(fetchAllStaff.fulfilled, (state, action) => {
        state.staffLoading = false;
        state.staffList = action.payload;
      })
      .addCase(fetchAllStaff.rejected, (state, action) => {
        state.staffLoading = false;
        state.staffError = action.payload;
      })

      .addCase(fetchAttendanceReport.pending, (state) => {
        state.attendanceLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAttendanceReport.fulfilled, (state, action) => {
        state.attendanceLoading = false;
        state.attendanceReport = action.payload;
        state.success = "Attendance report fetched successfully!";
        state.requestStatus = true;
      })
      .addCase(fetchAttendanceReport.rejected, (state, action) => {
        state.attendanceLoading = false;
        state.error = action.payload;
        state.attendanceReport = {};
        state.requestStatus = false;
      })

      .addCase(fetchSingleStaffAllAttendanceReport.pending, (state) => {
        state.singleStaffLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(
        fetchSingleStaffAllAttendanceReport.fulfilled,
        (state, action) => {
          state.singleStaffLoading = false;
          state.singleStaffAttendance = action.payload.attendance || [];
          state.singleStaffPagination = action.payload.pagination || {};
          state.singleStaffattendanceStatistics =
            action.payload.attendanceStatistics || {};
          state.success =
            "Single staff attendance report fetched successfully!";
          state.requestStatus = true;
        }
      )
      .addCase(
        fetchSingleStaffAllAttendanceReport.rejected,
        (state, action) => {
          state.singleStaffLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
          state.singleStaffAttendance = [];
          state.singleStaffPagination = {};
          state.singleStaffattendanceStatistics = {};
        }
      )
      .addCase(fetchPayrollListByStaffId.pending, (state) => {
        state.payrollLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchPayrollListByStaffId.fulfilled, (state, action) => {
        state.payrollLoading = false;
        state.staffPayrollList = action.payload.payslips || [];
        state.payrollPagination = action.payload.pagination || {};
        state.singleStaffattendanceStatistics =
          action.payload.attendanceStatistics || {};
        state.success = "Payroll list fetched successfully!";
        state.requestStatus = true;
      })
      .addCase(fetchPayrollListByStaffId.rejected, (state, action) => {
        state.payrollLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
        state.staffPayrollList = [];
        state.payrollPagination = {};
        state.singleStaffattendanceStatistics = {};
      })
      
      .addCase(fetchPayrollStaffList.pending, (state) => {
        state.payrollStaffLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchPayrollStaffList.fulfilled, (state, action) => {
        state.payrollStaffLoading = false;
        state.payrollStaffs = action.payload.staff || [];
        state.payrollStaffPagination = action.payload.pagination || null;
        state.success = "Payroll staff list fetched successfully!";
        state.requestStatus = true;
      })
      .addCase(fetchPayrollStaffList.rejected, (state, action) => {
        state.payrollStaffLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
        state.payrollStaffs = [];
        state.payrollStaffPagination = null;
      })

      .addCase(fetchPayrollHistoryList.pending, (state) => {
        state.payrollHistoryLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchPayrollHistoryList.fulfilled, (state, action) => {
        state.payrollHistoryLoading = false;
        state.payrollHistoryStaffs = action.payload.staff || [];
        state.payrollHistoryPagination = action.payload.pagination || null;
        state.success = "Payroll history list fetched successfully!";
        state.requestStatus = true;
      })
      .addCase(fetchPayrollHistoryList.rejected, (state, action) => {
        state.payrollHistoryLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
        state.payrollHistoryStaffs = [];
        state.payrollHistoryPagination = null;
      })
      .addCase(payStaffPayrollAction.pending, (state) => {
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(payStaffPayrollAction.fulfilled, (state, action) => {
        const { item_id } = action.payload;
        state.payrollStaffs = state.payrollStaffs.filter(
          (person) => person.id !== item_id
        );
        if (state.payrollStaffPagination) {
          state.payrollStaffPagination.total = Math.max(0, state.payrollStaffPagination.total - 1);
        }
        state.success = action.payload.message;
        state.requestStatus = true;
      })
      .addCase(payStaffPayrollAction.rejected, (state, action) => {
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearAttendancePayrollMessages } =
  attendancePayrollSlice.actions;
export default attendancePayrollSlice.reducer;
